import type { ReactNode } from 'react';
import { Columns3Icon, PlusIcon, XIcon } from 'lucide-react';
import type { Table } from '@tanstack/react-table';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  DataTableFacetedFilter,
  MULTI_VALUE_SEPARATOR,
} from '@/views/Order/components/table/TableFacetedFilter';
import type { ActiveFilter } from '@/views/Order/components/table/OrderToolbar';
import {
  parseDateRangeValue,
  serializeDateRangeValue,
} from '@/views/Order/utils/orderListFilters';
import { DateRangeFilter } from '@/components/FilterBar/DateRangeFilter';
import i18n from '@/utils/i18n';
import { PickedItemSearch } from './PickedItemSearch';
import { FILTER_UNSET } from './pickingJobFilters';
import type { PickingJobFilterDefinition, PickingJobRow } from './types';

interface PickingJobToolbarProps {
  table: Table<PickingJobRow>;
  availableFilters: PickingJobFilterDefinition[];
  activeFilters: ActiveFilter[];
  onFilterChange: (filterId: string, value: string) => void;
  onFilterAdd: (filterId: string) => void;
  onFilterRemove: (filterId: string) => void;
  search: string;
  onSearchChange: (value: string) => void;
  filterHint?: ReactNode;
}

export function PickingJobToolbar({
  table,
  availableFilters,
  activeFilters,
  onFilterChange,
  onFilterAdd,
  onFilterRemove,
  search,
  onSearchChange,
  filterHint,
}: PickingJobToolbarProps) {
  const addableFilters = availableFilters.filter(
    (f) => !activeFilters.some((af) => af.id === f.id),
  );

  const renderFilter = (af: ActiveFilter) => {
    const def = availableFilters.find((f) => f.id === af.id);
    if (!def) return null;
    const isRemovable = def.isRemovable !== false;
    const hasValue = af.value !== FILTER_UNSET;
    const showX = isRemovable || hasValue;
    const label = def.getLabel();

    return (
      <div
        key={af.id}
        className={`tw:flex tw:items-center tw:gap-0 ${
          def.inactiveNote ? 'tw:opacity-60' : ''
        }`}
        title={def.inactiveNote}
      >
        {def.kind === 'dateRange' ? (
          <DateRangeFilter
            variant="toolbar"
            placeholder={label}
            value={parseDateRangeValue(af.value)}
            onChange={(range) =>
              onFilterChange(af.id, serializeDateRangeValue(range ?? {}))
            }
            className={showX ? 'tw:rounded-r-none' : undefined}
          />
        ) : def.multiSelect ? (
          <DataTableFacetedFilter
            title={label}
            options={def.options}
            multiple
            value={af.value}
            onValueChange={(value) =>
              onFilterChange(
                af.id,
                Array.isArray(value)
                  ? value.join(MULTI_VALUE_SEPARATOR)
                  : value,
              )
            }
            triggerClassName={showX && 'tw:rounded-r-none'}
          />
        ) : (
          <DataTableFacetedFilter
            title={label}
            options={def.options}
            value={af.value}
            onValueChange={(value) => onFilterChange(af.id, value)}
            triggerClassName={showX && 'tw:rounded-r-none'}
          />
        )}
        {showX && (
          <Button
            variant="outline"
            size="icon"
            className="tw:rounded-l-none tw:border-l-0 tw:size-8"
            onClick={() =>
              isRemovable
                ? onFilterRemove(af.id)
                : onFilterChange(af.id, FILTER_UNSET)
            }
          >
            <XIcon className="tw:size-3" />
            <span className="tw:sr-only">
              {isRemovable
                ? i18n.t('Picking:removeFilter', { filter: label })
                : i18n.t('Picking:clearFilter', { filter: label })}
            </span>
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-8">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
        {activeFilters.map(renderFilter)}
        {addableFilters.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm">
                <PlusIcon />
                {i18n.t('Order:toolbarAddFilter')}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {addableFilters.map((f) => (
                <DropdownMenuItem key={f.id} onClick={() => onFilterAdd(f.id)}>
                  {f.getLabel()}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {!!filterHint && (
        <p className="tw:-mt-6 tw:text-sm tw:text-muted-foreground">
          {filterHint}
        </p>
      )}

      <Separator />

      <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
        <PickedItemSearch value={search} onChange={onSearchChange} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="icon">
              <Columns3Icon />
              <span className="tw:sr-only">
                {i18n.t('Picking:toggleColumns')}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              {i18n.t('Picking:toggleColumns')}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {table
              .getAllColumns()
              .filter(
                (column) =>
                  typeof column.accessorFn !== 'undefined' &&
                  column.getCanHide(),
              )
              .map((column) => {
                const def = availableFilters.find((f) => f.id === column.id);
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {def?.getLabel() ?? column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
